import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Table, Card, Spinner, Alert, Form, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faEye, faTrash } from '@fortawesome/free-solid-svg-icons';
import {
  EMPTY_TASK_FILTER_OPTIONS,
  deleteTask,
  fetchTaskFilters,
  fetchTasks,
} from '../../redux/actions/taskActions';
import Pagination from '../common/Pagination';
import useDebounce from '../../hooks/useDebounce';
import { ApiVersionBadge, EndpointBadge, ErrorBadge, StatusBadge } from './taskBadges';

const TaskList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [taskIdSearch, setTaskIdSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [endpointFilter, setEndpointFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [fallbackFilter, setFallbackFilter] = useState('');
  const [vpnFilter, setVpnFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appVersionFilter, setAppVersionFilter] = useState('');
  const [countryCodeFilter, setCountryCodeFilter] = useState('');
  const [deviceIdFilter, setDeviceIdFilter] = useState('');
  const [rcAppUserIdFilter, setRcAppUserIdFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [osFilter, setOsFilter] = useState('');
  const [osVersionFilter, setOsVersionFilter] = useState('');
  const [apiVersionFilter, setApiVersionFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState(EMPTY_TASK_FILTER_OPTIONS);

  const debouncedTaskId = useDebounce(taskIdSearch, 500);
  const debouncedDeviceId = useDebounce(deviceIdFilter, 500);
  const debouncedRcAppUserId = useDebounce(rcAppUserIdFilter, 500);
  const debouncedEmail = useDebounce(emailFilter, 500);

  const { items: tasks, loading, total, error } = useSelector((state) => ({
    items: state.tasks.items || [],
    loading: state.tasks.loading,
    total: state.tasks.total || 0,
    error: state.tasks.error,
  }));

  useEffect(() => {
    let cancelled = false;

    const loadFilterOptions = async () => {
      try {
        const options = await fetchTaskFilters();
        if (!cancelled) {
          setFilterOptions(options);
        }
      } catch {
        if (!cancelled) {
          setFilterOptions(EMPTY_TASK_FILTER_OPTIONS);
        }
      }
    };

    loadFilterOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const buildFetchParams = () => {
    const params = { page: currentPage, items_per_page: pageSize };
    if (debouncedTaskId) params.task_id = debouncedTaskId;
    if (statusFilter) params.status = statusFilter;
    if (endpointFilter) params.endpoint = endpointFilter;
    if (platformFilter) params.platform = platformFilter;
    if (fallbackFilter !== '') params.fallback_used = fallbackFilter;
    if (vpnFilter !== '') params.is_vpn = vpnFilter;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;
    if (appVersionFilter) params.app_version = appVersionFilter;
    if (countryCodeFilter) params.country_code = countryCodeFilter;
    if (debouncedDeviceId) params.device_id = debouncedDeviceId;
    if (debouncedRcAppUserId) params.rc_app_user_id = debouncedRcAppUserId;
    if (debouncedEmail) params.email = debouncedEmail;
    if (osFilter) params.os = osFilter;
    if (osVersionFilter) params.os_version = osVersionFilter;
    if (apiVersionFilter) params.api_version = apiVersionFilter;
    return params;
  };

  useEffect(() => {
    dispatch(fetchTasks(buildFetchParams()));
  }, [
    dispatch, currentPage, pageSize, debouncedTaskId, statusFilter, endpointFilter, platformFilter,
    fallbackFilter, vpnFilter, dateFrom, dateTo, appVersionFilter, countryCodeFilter,
    debouncedDeviceId, debouncedRcAppUserId, debouncedEmail, osFilter, osVersionFilter,
    apiVersionFilter,
  ]);

  const handleDeleteTask = async (taskId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete task ${taskId}? This cannot be undone.`)) {
      return;
    }
    try {
      const ok = await deleteTask(taskId);
      if (ok) {
        dispatch(fetchTasks(buildFetchParams()));
      } else {
        // eslint-disable-next-line no-alert
        window.alert('Failed to delete task');
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e.response?.data?.message || 'Failed to delete task');
    }
  };

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const handleTaskIdChange = (e) => {
    setTaskIdSearch(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatMs = (ms) => {
    if (ms == null) return '—';
    if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
    return `${ms}ms`;
  };

  const taskIsVpn = (task) => Boolean(task?.attributes?.is_vpn);

  const renderVpnBadge = (task) => (
    taskIsVpn(task)
      ? <Badge bg="info" text="dark">VPN</Badge>
      : <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>No</Badge>
  );

  const renderDetailButton = (taskId, label) => (
    <Button
      variant="outline-info"
      size="sm"
      onClick={() => navigate(`/tasks/${taskId}`)}
      title="View Detail"
    >
      <FontAwesomeIcon icon={faEye} className={label ? 'me-1' : undefined} />
      {label || null}
    </Button>
  );

  const renderDeleteButton = (taskId) => (
    <Button
      variant="outline-danger"
      size="sm"
      onClick={() => handleDeleteTask(taskId)}
      title="Delete task"
    >
      <FontAwesomeIcon icon={faTrash} />
    </Button>
  );

  const renderMetaItem = (label, value) => (
    <div className="task-card__meta-item">
      <span className="task-card__meta-label">{label}</span>
      <span className="task-card__meta-value">{value ?? '—'}</span>
    </div>
  );

  const renderMobileTaskList = () => {
    if (loading) {
      return (
        <div className="text-center py-4 task-mobile-only">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      );
    }

    if (tasks.length === 0) {
      return <div className="text-center text-muted py-4 task-mobile-only">No tasks found</div>;
    }

    return (
      <div className="task-card-list">
        {tasks.map((task) => (
          <Card key={task.task_id} className="task-card mb-2">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <StatusBadge status={task.status} />
                <div className="d-flex gap-1">
                  {renderDetailButton(task.task_id)}
                  {renderDeleteButton(task.task_id)}
                </div>
              </div>
              <code className="task-card__task-id d-block mb-2">{task.task_id}</code>
              <div className="task-card__meta">
                {renderMetaItem('Endpoint', <EndpointBadge endpoint={task.endpoint} />)}
                {renderMetaItem('API', <ApiVersionBadge task={task} />)}
                {renderMetaItem('Platform', task.platform)}
                {renderMetaItem('Final Vendor', task.final_vendor)}
                {renderMetaItem('VPN', renderVpnBadge(task))}
                {renderMetaItem('Fallback', task.fallback_used ? (
                  <Badge bg="warning" text="dark">Yes</Badge>
                ) : (
                  <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>No</Badge>
                ))}
                {renderMetaItem('Duration', formatMs(task.total_duration_ms))}
                {renderMetaItem('Error', task.error_code ? (
                  <ErrorBadge>{task.error_code}</ErrorBadge>
                ) : '—')}
                {renderMetaItem('Created', formatDate(task.created_at))}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Task Manager</h2>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      <Card className="task-list-card">
        <Card.Body className="task-list-card__body">
          {/* Filters row 1 */}
          <div className="d-flex flex-wrap gap-3 mb-3 task-filters">
            <div className="flex-grow-1 task-filters__search">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faSearch} />
                </span>
                <Form.Control
                  type="text"
                  placeholder="Search by task ID..."
                  value={taskIdSearch}
                  onChange={handleTaskIdChange}
                />
              </div>
            </div>
            <div className="task-filters__field">
              <Form.Select value={statusFilter} onChange={handleFilterChange(setStatusFilter)}>
                <option value="">All Status</option>
                <option value="received">Received</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Select value={endpointFilter} onChange={handleFilterChange(setEndpointFilter)}>
                <option value="">All Endpoints</option>
                <option value="recognize">Recognize</option>
                <option value="recognize_backup">Recognize Backup (AI)</option>
                <option value="estimate_price">Estimate Price (AI)</option>
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Select value={platformFilter} onChange={handleFilterChange(setPlatformFilter)}>
                <option value="">All Platforms</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Select value={fallbackFilter} onChange={handleFilterChange(setFallbackFilter)}>
                <option value="">Fallback — Any</option>
                <option value="true">Fallback Yes</option>
                <option value="false">Fallback No</option>
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Select value={vpnFilter} onChange={handleFilterChange(setVpnFilter)}>
                <option value="">VPN — Any</option>
                <option value="true">VPN detected</option>
                <option value="false">Not VPN</option>
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Select value={apiVersionFilter} onChange={handleFilterChange(setApiVersionFilter)}>
                <option value="">API — All</option>
                <option value="v2">V2 (server recognize)</option>
                <option value="v3">V3 (client payload)</option>
              </Form.Select>
            </div>
          </div>

          {/* Filters row 2 — client device */}
          <div className="d-flex flex-wrap gap-3 mb-3 task-filters">
            <div className="task-filters__field">
              <Form.Label className="mb-1 small text-muted">App Version</Form.Label>
              <Form.Select value={appVersionFilter} onChange={handleFilterChange(setAppVersionFilter)}>
                <option value="">All App Versions</option>
                {(filterOptions.app_versions || []).map((version) => (
                  <option key={version} value={version}>{version}</option>
                ))}
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Label className="mb-1 small text-muted">Country Code</Form.Label>
              <Form.Select value={countryCodeFilter} onChange={handleFilterChange(setCountryCodeFilter)}>
                <option value="">All Countries</option>
                {(filterOptions.country_codes || []).map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Label className="mb-1 small text-muted">OS</Form.Label>
              <Form.Select value={osFilter} onChange={handleFilterChange(setOsFilter)}>
                <option value="">All OS</option>
                {(filterOptions.os_list || []).map((os) => (
                  <option key={os} value={os}>{os}</option>
                ))}
              </Form.Select>
            </div>
            <div className="task-filters__field">
              <Form.Label className="mb-1 small text-muted">OS Version</Form.Label>
              <Form.Select value={osVersionFilter} onChange={handleFilterChange(setOsVersionFilter)}>
                <option value="">All OS Versions</option>
                {(filterOptions.os_versions || []).map((version) => (
                  <option key={version} value={version}>{version}</option>
                ))}
              </Form.Select>
            </div>
            <div className="task-filters__field flex-grow-1">
              <Form.Label className="mb-1 small text-muted">Device ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="Device ID..."
                value={deviceIdFilter}
                onChange={(e) => {
                  setDeviceIdFilter(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="task-filters__field flex-grow-1">
              <Form.Label className="mb-1 small text-muted">RC App User ID</Form.Label>
              <Form.Control
                type="text"
                placeholder="RC app user ID..."
                value={rcAppUserIdFilter}
                onChange={(e) => {
                  setRcAppUserIdFilter(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="task-filters__field flex-grow-1">
              <Form.Label className="mb-1 small text-muted">Email</Form.Label>
              <Form.Control
                type="text"
                placeholder="Email..."
                value={emailFilter}
                onChange={(e) => {
                  setEmailFilter(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Filters row 3 — date range */}
          <div className="d-flex flex-wrap gap-3 mb-4 task-filters task-filters--dates">
            <div className="task-filters__date">
              <Form.Label className="mb-1 small text-muted">Date From</Form.Label>
              <Form.Control type="date" value={dateFrom} onChange={handleFilterChange(setDateFrom)} />
            </div>
            <div className="task-filters__date">
              <Form.Label className="mb-1 small text-muted">Date To</Form.Label>
              <Form.Control type="date" value={dateTo} onChange={handleFilterChange(setDateTo)} />
            </div>
          </div>

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            position="top"
          />

          {renderMobileTaskList()}

          <div className="task-table-wrapper">
            <Table hover className="table-vcenter task-table">
              <thead>
                <tr>
                  <th className="task-table__task-id">Task ID</th>
                  <th>API</th>
                  <th>Endpoint</th>
                  <th>Status</th>
                  <th>Platform</th>
                  <th>Final Vendor</th>
                  <th>VPN</th>
                  <th>Fallback</th>
                  <th>Duration</th>
                  <th className="task-table__error">Error</th>
                  <th className="task-table__created">Created</th>
                  <th className="task-table__actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="12" className="text-center py-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                    </td>
                  </tr>
                ) : tasks.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="text-center text-muted py-4">No tasks found</td>
                  </tr>
                ) : (
                  tasks.map((task) => (
                    <tr key={task.task_id}>
                      <td className="task-table__task-id">
                        <code
                          className="task-table__task-id-text"
                          title={task.task_id}
                        >
                          {task.task_id}
                        </code>
                      </td>
                      <td>
                        <ApiVersionBadge task={task} />
                      </td>
                      <td>
                        <EndpointBadge endpoint={task.endpoint} />
                      </td>
                      <td>
                        <StatusBadge status={task.status} />
                      </td>
                      <td>{task.platform || '—'}</td>
                      <td>{task.final_vendor || '—'}</td>
                      <td>{renderVpnBadge(task)}</td>
                      <td>
                        {task.fallback_used ? (
                          <Badge bg="warning" text="dark">Yes</Badge>
                        ) : (
                          <Badge style={{ backgroundColor: '#6c757d', color: '#fff' }}>No</Badge>
                        )}
                      </td>
                      <td>{formatMs(task.total_duration_ms)}</td>
                      <td className="task-table__error">
                        {task.error_code ? (
                          <div>
                            <ErrorBadge>{task.error_code}</ErrorBadge>
                            {task.client_report_tag && (
                              <div className="text-muted small mt-1 text-truncate" title={task.client_report_tag}>
                                tag: {task.client_report_tag}
                              </div>
                            )}
                          </div>
                        ) : '—'}
                      </td>
                      <td className="task-table__created">{formatDate(task.created_at)}</td>
                      <td className="task-table__actions">
                        <div className="d-flex gap-1">
                          {renderDetailButton(task.task_id, '')}
                          {renderDeleteButton(task.task_id)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalItems={total}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            position="bottom"
          />
        </Card.Body>
      </Card>
    </div>
  );
};

export default TaskList;
